import { logger } from '@/services/logger'
import type { Quantity, StandardizedQuantity, Unit } from '@/types/nutrition'

/**
 * Unit conversion mappings with their multipliers for amount adjustments
 */
interface UnitConversion {
  standardUnit: Unit
  multiplier: number
}

/**
 * Comprehensive mapping of all possible unit variations to standard forms
 */
const UNIT_CONVERSIONS: Record<string, UnitConversion> = {
  // Mass units - all convert to 'g'
  g: { standardUnit: 'g', multiplier: 1 },
  gr: { standardUnit: 'g', multiplier: 1 }, // Common abbreviation
  gram: { standardUnit: 'g', multiplier: 1 },
  grams: { standardUnit: 'g', multiplier: 1 },
  'gram(s)': { standardUnit: 'g', multiplier: 1 }, // Parenthetical form
  граммов: { standardUnit: 'g', multiplier: 1 }, // Cyrillic grams
  грамм: { standardUnit: 'g', multiplier: 1 }, // Cyrillic gram
  граммах: { standardUnit: 'g', multiplier: 1 }, // Cyrillic grams (in)
  kg: { standardUnit: 'g', multiplier: 1000 },
  kilogram: { standardUnit: 'g', multiplier: 1000 },
  kilograms: { standardUnit: 'g', multiplier: 1000 },

  // Volume units - all convert to 'ml'
  ml: { standardUnit: 'ml', multiplier: 1 },
  milliliter: { standardUnit: 'ml', multiplier: 1 },
  milliliters: { standardUnit: 'ml', multiplier: 1 },
  l: { standardUnit: 'ml', multiplier: 1000 },
  liter: { standardUnit: 'ml', multiplier: 1000 },
  liters: { standardUnit: 'ml', multiplier: 1000 },

  // Count units - all convert to 'piece'
  piece: { standardUnit: 'piece', multiplier: 1 },
  pieces: { standardUnit: 'piece', multiplier: 1 },
  slice: { standardUnit: 'piece', multiplier: 1 },
  slices: { standardUnit: 'piece', multiplier: 1 },
  fruit: { standardUnit: 'piece', multiplier: 1 },
  fruits: { standardUnit: 'piece', multiplier: 1 },
  capsule: { standardUnit: 'piece', multiplier: 1 },
  capsules: { standardUnit: 'piece', multiplier: 1 },
  tablet: { standardUnit: 'piece', multiplier: 1 },
  tablets: { standardUnit: 'piece', multiplier: 1 },
  cup: { standardUnit: 'piece', multiplier: 1 },
  cups: { standardUnit: 'piece', multiplier: 1 },
  serving: { standardUnit: 'piece', multiplier: 1 },
  servings: { standardUnit: 'piece', multiplier: 1 },

  // Size-based count units - convert to pieces
  small: { standardUnit: 'piece', multiplier: 1 },
  medium: { standardUnit: 'piece', multiplier: 1 },
  large: { standardUnit: 'piece', multiplier: 1 },
  'extra large': { standardUnit: 'piece', multiplier: 1 },
}

/**
 * Standardizes a quantity by converting complex units to simple standard forms
 *
 * @param quantity - The original quantity with potentially complex unit
 * @returns Standardized quantity with simple unit ('g', 'ml', or 'piece')
 *
 * @example
 * ```typescript
 * // Weight conversions
 * standardizeQuantity({ amount: 300, unit: 'граммов' }) // → { amount: 300, unit: 'g' }
 * standardizeQuantity({ amount: 1.5, unit: 'kg' }) // → { amount: 1500, unit: 'g' }
 *
 * // Volume conversions
 * standardizeQuantity({ amount: 350, unit: 'milliliter' }) // → { amount: 350, unit: 'ml' }
 *
 * // Count conversions
 * standardizeQuantity({ amount: 4, unit: 'fruit' }) // → { amount: 4, unit: 'piece' }
 * standardizeQuantity({ amount: 1, unit: 'medium' }) // → { amount: 1, unit: 'piece' }
 * ```
 */
export const standardizeQuantity = (quantity: Quantity): StandardizedQuantity => {
  const unitKey = quantity.unit.toLowerCase().trim()
  const conversion = UNIT_CONVERSIONS[unitKey]

  if (!conversion) {
    // If unit is not recognized, treat as piece (count unit)
    logger.warn(`Unknown unit "${quantity.unit}" standardized to "piece"`)
    return {
      amount: quantity.amount,
      unit: 'piece',
    }
  }

  return {
    amount: quantity.amount * conversion.multiplier,
    unit: conversion.standardUnit,
  }
}

/**
 * Normalizes a raw unit string to a standardized unit before parsing
 * This is used by parsers to handle unit variations during initial parsing
 *
 * @param rawUnit - The raw unit string from parsed data
 * @returns The standardized unit ('g', 'ml', or 'piece')
 *
 * @example
 * ```typescript
 * normalizeUnit('граммов') // → 'g'
 * normalizeUnit('milliliters') // → 'ml'
 * normalizeUnit('medium') // → 'piece'
 * ```
 */
export const normalizeUnit = (rawUnit: string): Unit => {
  const unitKey = rawUnit.toLowerCase().trim()
  const conversion = UNIT_CONVERSIONS[unitKey]

  if (!conversion) {
    logger.warn(`Unknown unit "${rawUnit}" normalized to "piece"`)
    return 'piece'
  }

  return conversion.standardUnit
}

/**
 * Gets the conversion multiplier for a given unit
 * Useful for understanding how amounts will be adjusted during standardization
 *
 * @param unit - The unit to get multiplier for
 * @returns The multiplier used when converting to standard unit
 */
export const getUnitMultiplier = (unit: string): number => {
  const unitKey = unit.toLowerCase().trim()
  const conversion = UNIT_CONVERSIONS[unitKey]
  return conversion?.multiplier ?? 1
}

/**
 * Checks if a unit is already in standardized form
 *
 * @param unit - The unit to check
 * @returns True if unit is already standardized ('g', 'ml', or 'piece')
 */
export const isStandardizedUnit = (unit: string): unit is Unit => {
  return unit === 'g' || unit === 'ml' || unit === 'piece'
}
